/**
 * Stream Sources Configuration
 * 
 * This file defines available streaming sources for instant playback.
 * Each source provides a URL pattern that can be used to generate
 * streaming URLs for specific movies.
 */

import { getApiBaseUrl } from './api';

export interface StreamSource {
  id: string;
  name: string;
  urlPattern: string; // Use {tmdbId} as placeholder
  description?: string;
  badge?: string;
  icon?: string; // Icon name or emoji
  color?: string; // Brand color for UI
  autoPlay?: boolean; // Auto-play video
  fullscreen?: boolean; // Auto-fullscreen
  additionalParams?: Record<string, string>; // Additional URL parameters
}

export const STREAM_SOURCES: StreamSource[] = [
  {
    id: 'aiostreams',
    name: 'Main Source',
    urlPattern: '/api/aiostreams/stream/{tmdbId}',
    description: 'Instant streaming',
    badge: 'HD',
    icon: '🎬',
    color: 'blue',
    autoPlay: false,
    fullscreen: false
  },
  {
    id: 'vidsrc',
    name: 'VidSrc',
    urlPattern: '/api/vidsrc/stream/{tmdbId}',
    description: 'Direct download with quality selection',
    badge: 'HD',
    icon: '🎬',
    color: 'purple',
    autoPlay: false,
    fullscreen: false
  },
  {
    id: 'vidfast',
    name: 'VidFast',
    urlPattern: 'https://vidfast.pro/movie/{tmdbId}',
    description: 'Instant streaming',
    badge: 'HD',
    icon: '🎬',
    color: 'red',
    autoPlay: true,
    fullscreen: true,
    additionalParams: {
      autoPlay: 'true'
    }
  },
  {
    id: 'vidlink',
    name: 'VidLink',
    urlPattern: 'https://vidlink.pro/movie/{tmdbId}',
    description: 'Instant streaming',
    badge: 'HD',
    icon: '🎬',
    color: 'purple',
    autoPlay: true,
    fullscreen: true
  }
  // Future sources can be added here:
  // {
  //   id: 'another-source',
  //   name: 'Another Source',
  //   urlPattern: 'https://example.com/movie/{tmdbId}',
  //   description: 'Another streaming option',
  //   badge: '4K',
  //   icon: '📺',
  //   color: 'blue'
  // }
];

/**
 * Generate streaming URL for a specific source and movie
 */
export function getStreamUrl(sourceId: string, tmdbId: number): string | null {
  const source = STREAM_SOURCES.find(s => s.id === sourceId);
  if (!source) return null;
  
  let url = source.urlPattern.replace('{tmdbId}', tmdbId.toString());
  
  // If URL is relative (starts with /), prepend API base URL
  if (url.startsWith('/')) {
    const baseUrl = getApiBaseUrl();
    url = `${baseUrl}${url}`;
  }
  
  // Add additional parameters if specified
  if (source.additionalParams) {
    const params = new URLSearchParams();
    Object.entries(source.additionalParams).forEach(([key, value]) => {
      params.append(key, value);
    });
    url += `?${params.toString()}`;
  }
  
  return url;
}

/**
 * Get all available stream sources
 */
export function getStreamSources(): StreamSource[] {
  return STREAM_SOURCES;
}

/**
 * Get a specific stream source by ID
 */
export function getStreamSource(sourceId: string): StreamSource | null {
  return STREAM_SOURCES.find(s => s.id === sourceId) || null;
}

/**
 * Build a watch URL for instant playback with automatic source resolution.
 * The watch page will race sources and pick the first one that actually has the movie.
 */
export function buildInstantPlayUrl(
  tmdbId: number,
  movie: { title: string; poster_path?: string | null; backdrop_path?: string | null; overview?: string | null; release_date?: string | null; vote_average?: number | null }
): string {
  const params = new URLSearchParams({
    source: 'auto',
    title: movie.title,
    posterPath: movie.poster_path || '',
    backdropPath: movie.backdrop_path || '',
    overview: movie.overview || '',
    releaseDate: movie.release_date || '',
    voteAverage: movie.vote_average?.toString() || '',
  });
  return `/watch/${tmdbId}?${params.toString()}`;
}
